import { useEffect, useState } from 'react'

import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { UNKNOWN_ERROR } from '@/gf/providers/MsgsProvider'
import useUpdatePartRequest from '../hooks/useUpdatePartRequest'

import Action from '@/gf/components/Action'
import Modal from '@/gf/components/Modal'

import { PartRequest } from '../../types'
import JobCodeM from '@/gf/modules/JobCode'
import PartForm, { Errors, Form, FormErrors, initForm } from './PartForm'

const EditPartModal = ({
  partRequest,
  onClose,
  onEdited,
  showRfqQty = false,
  useCluePartSearch = false,
  jobCodeWorkOrderId,
}: {
  partRequest?: PartRequest
  onClose: () => void
  onEdited: () => void
  showRfqQty?: boolean
  useCluePartSearch?: boolean
  /** M5 work order this request is on — set only for AssetWorks orgs with a work
   * order, and the only case where the job-code picker has anything to offer. */
  jobCodeWorkOrderId?: string
}) => {
  const [_, msgr] = useMsgs()
  const [submitting, submittingToggler] = useToggle(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [form, setForm] = useState<Form>(initForm)
  const updatePartRequest = useUpdatePartRequest()

  useEffect(() => {
    if (partRequest) {
      setForm({
        mpn: partRequest.mpn,
        description: partRequest.description,
        quantity: partRequest.quantity,
        // Seed the picker with the code M5 actually holds, falling back to what was
        // picked in Gearflow — see JobCode module (GEA-6990).
        ...(jobCodeWorkOrderId ? { taskNumber: JobCodeM.of(partRequest) } : {}),
        ...(showRfqQty ? { rfqQuantity: partRequest.rfqQuantity ?? partRequest.quantity } : {}),
      })
      setErrors({})
    }
  }, [!partRequest])

  const onSubmit = () => {
    if (!partRequest || submitting) return
    submittingToggler.on()

    updatePartRequest({ partRequestId: partRequest.id, ...form })
      .then(() => {
        onEdited()
        msgr.add('Part saved.', 'positive')
      })
      .catch((err) => {
        const newErrors = Errors.fromGqlErrors(err.graphQLErrors)
        setErrors(newErrors)

        if (Object.keys(newErrors).length === 0) {
          msgr.add(err.message || UNKNOWN_ERROR, 'negative')
        }
      })
      .finally(() => {
        submittingToggler.off()
      })
  }

  return (
    <Modal
      title="Edit Part"
      open={!!partRequest}
      onClose={onClose}
      footerAction={
        <Action.P type="submit" form="edit-part" disabled={submitting}>
          {submitting ? <>Saving&hellip;</> : 'Save'}
        </Action.P>
      }
    >
      <PartForm
        id="edit-part"
        form={form}
        errors={errors}
        onChange={(newForm) => setForm(newForm)}
        onSubmit={onSubmit}
        useCluePartSearch={useCluePartSearch}
        jobCodeWorkOrderId={jobCodeWorkOrderId}
      />
    </Modal>
  )
}

export default EditPartModal
